import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Phone, Mail, MapPin, Clock, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export const Contatti = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          subject: formData.subject,
          message: formData.message,
          source: 'contatti',
        }),
      });

      if (response.ok) {
        toast({
          title: 'Messaggio Inviato!',
          description: 'Ti risponderemo il prima possibile.',
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
        setPrivacyAccepted(false);
      } else {
        throw new Error('Errore durante l\'invio');
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Si è verificato un errore. Riprova più tardi.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Telefono',
      content: '0124 572040',
      link: 'tel:+390124572040',
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'firm.recupero@gmail.com',
      link: 'mailto:firm.recupero@gmail.com',
    },
    {
      icon: MapPin,
      title: 'Sede',
      content: 'Canavese, provincia di Torino',
      link: null,
    },
    {
      icon: Clock,
      title: 'Orari',
      content: 'Lun-Ven: 9:00-12:30 / 14:00-18:00',
      link: null,
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section Premium */}
      <section className="relative py-24 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        </div>
        <div className="absolute inset-0 pattern-dots opacity-20"></div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-6xl font-bold mb-6 animate-fade-in">Contattaci</h1>
            <p className="text-xl text-white/90 animate-fade-in leading-relaxed" style={{ animationDelay: '0.1s' }}>
              Siamo qui per rispondere a tutte le tue domande e fornirti il supporto di cui hai bisogno
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card
                  key={index}
                  className="border-2 hover:border-[var(--copper-primary)] transition-all duration-300 hover:shadow-xl animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{info.title}</h3>
                    {info.link ? (
                      <a
                        href={info.link}
                        className="text-[var(--text-light)] hover:text-[var(--copper-primary)] transition-colors"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-[var(--text-light)]">{info.content}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Contact Form and Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="animate-slide-left">
              <Card className="border-2">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold mb-6">Invia un Messaggio</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name">Nome e Cognome *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Mario Rossi"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="mario.rossi@example.com"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Telefono</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+39 XXX XXX XXXX"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject">Oggetto *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Richiesta quotazione"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Messaggio *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Scrivi qui il tuo messaggio..."
                        rows={5}
                        className="mt-2"
                      />
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="privacy-contatti"
                        checked={privacyAccepted}
                        onCheckedChange={(checked) => setPrivacyAccepted(checked)}
                        className="mt-0.5 border-gray-300 data-[state=checked]:bg-[var(--copper-primary)] data-[state=checked]:border-[var(--copper-primary)]"
                        data-testid="privacy-checkbox-contatti"
                      />
                      <Label htmlFor="privacy-contatti" className="text-sm text-[var(--text-light)] leading-relaxed cursor-pointer">
                        Ho letto e accetto la <a href="https://www.iubenda.com/privacy-policy/73606221" className="iubenda-white iubenda-noiframe iubenda-embed text-[var(--copper-primary)] hover:text-[var(--copper-dark)] underline font-medium" title="Privacy Policy">Privacy Policy</a> *
                      </Label>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting || !privacyAccepted}
                      className="w-full bg-[var(--copper-primary)] hover:bg-[var(--copper-dark)] text-white disabled:opacity-50"
                      data-testid="submit-contatti"
                    >
                      {isSubmitting ? 'Invio in corso...' : 'Invia Messaggio'}
                    </Button>

                    <p className="text-sm text-[var(--text-light)] text-center">
                      I tuoi dati saranno trattati nel rispetto del GDPR
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Info */}
            <div className="animate-slide-right">
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-bold mb-6">Perché Contattarci?</h2>
                  <div className="space-y-4">
                    {[
                      'Richiesta di quotazione per cavi elettrici',
                      'Informazioni sui nostri servizi',
                      'Collaborazioni e partnership',
                      'Qualsiasi altra domanda o esigenza',
                    ].map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle2 className="h-6 w-6 text-[var(--verdigris)] flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-[var(--text-light)]">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Card className="bg-gradient-to-br from-[var(--warm-gray)] to-white border-2">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold mb-4">Referente</h3>
                    <p className="text-lg mb-2">
                      <strong>Andrea Cossavella</strong>
                    </p>
                    <p className="text-[var(--text-light)] mb-4">Responsabile Commerciale</p>
                    <div className="space-y-2">
                      <a
                        href="tel:+390124572040"
                        className="flex items-center space-x-2 text-[var(--copper-primary)] hover:text-[var(--copper-dark)] transition-colors"
                      >
                        <Phone className="h-5 w-5" />
                        <span>0124 572040</span>
                      </a>
                      <a
                        href="mailto:firm.recupero@gmail.com"
                        className="flex items-center space-x-2 text-[var(--copper-primary)] hover:text-[var(--copper-dark)] transition-colors"
                      >
                        <Mail className="h-5 w-5" />
                        <span>firm.recupero@gmail.com</span>
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] text-white border-0">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold mb-4">Tempi di Risposta</h3>
                    <p className="text-white/90 leading-relaxed">
                      Ci impegniamo a rispondere a tutte le richieste entro <strong>24 ore lavorative</strong>.
                      Per urgenze, non esitare a contattarci telefonicamente.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};